import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SrsVoteColumnComponent } from './srs-vote-column/srs-vote-column.component';
import { SrsHistoryColumnComponent } from './srs-history-column/srs-history-column.component';
import { filter, Subject, Subscription, take } from 'rxjs';
import { SrsProxyVoteComponent } from '../../srs-proxy-vote/srs-proxy-vote.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { appConstants } from '../../../../../shared/constants/app-constants';
import { Country } from '../../../../../shared/constants/srs-ballot.constants';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { IOption } from '../../../../controls/models/option';
import { ITableColumn, TableColumnType } from '../../../../controls/table/table-column';
import { ISrsBallotDetail, SrsBallotStatusId } from '../../../shared/models/sr/srs-ballot-details';
import { ISrsBallotVote } from '../../../shared/models/sr/srs-vote';
@Component({
    selector: 'app-srs-votes',
    templateUrl: './srs-votes.component.html',
    styleUrl: './srs-votes.component.scss',
    standalone: false
})
export class SrsVotesComponent implements OnInit, OnDestroy {
  @Input() srsBallotDetail: ISrsBallotDetail | null = null;
  @Output() afterSave = new EventEmitter<string>();
  afterSaveSubject: Subject<string> = new Subject<string>();

  allVotes: ISrsBallotVote[] = [];
  showingVotes: ISrsBallotVote[] = [];
  selectedVotes: ISrsBallotVote[] = [];
  votingRightDropdown: string = '';
  locationDropdown: string = '';
  isShowingLocations: boolean = true;
  isJoint: boolean = false;
  selectedVoteCount: number = 0;
  isUserPmOrAbove: boolean = false;
  isUserCM: boolean = false;
  isHoSD: boolean = false;
  isUserPmOrAboveOrHoSD: boolean = false;
  currentUser: IUser | null = null;
  currentUserCommitteeCodes: string[] = [];
  subscriptions: Subscription = new Subscription();
  loginUserEmail: string = '';
  isDropdownChanged: boolean = false;
  selectedPageNumber: number = 1;
  pageSize: number = 50;
  totalRecords: number = 0;
  eligible = appConstants.votingRights.eligible;
  notEligible = appConstants.votingRights.notEligible;
  ballotInOpenOrDecisionPendingStatus: boolean = false;


  votingRightOptions: IOption<string>[] = [
    { value: 'eligible', label: this.eligible },
    { value: 'notEligible', label: this.notEligible },
  ];

  locationOptions: IOption<string>[] = [
    { value: 'aus-nz', label: 'AUS & NZ' },
    { value: 'au', label: 'AUS' },
    { value: 'nz', label: 'NZ' },
  ];


  constructor(
    public _authService: AuthService,
    private _samlService: SamlService,
    private _modalService: NgbModal,
  ) {
    this.initUser();
    this.setupAfterSaveSubjectSubscription();
  }

  initUser() {
    const currentUserSubscription =
      this._authService.currentUserSubject
      .pipe(
        filter(user => !!user),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        if (this.currentUser) {
          this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
          this.isUserCM = UserUtility.isCommitteeMember(this.currentUser);
          this.loginUserEmail = this.currentUser.email;
          if(!this.isUserPmOrAbove) {
            this._samlService
            .getLoggedInUserHoSDStatus()
            .subscribe((response) => {
              this.isHoSD = response.content;
              this.isUserPmOrAboveOrHoSD = this.isUserPmOrAbove || this.isHoSD;
            });
          }else {
            this.isUserPmOrAboveOrHoSD = true
          }
          this.updateTableColumns();
        }
      });
    this.subscriptions.add(currentUserSubscription);
  }

  setupAfterSaveSubjectSubscription() {
    this.subscriptions.add(this.afterSaveSubject.subscribe((response) => {
      if(response) {
        this.afterSave.emit(response);
      }
    }));
  }


  viewVoteTableColumns: ITableColumn<ISrsBallotVote>[] = [
    {
      name: 'checkbox',
      displayName: 'value',
      isSortable: false,
      type: TableColumnType.checkbox,
      getDisplayValue: (row: ISrsBallotVote) => row.isEligible,
      getIsCheckBoxVisible: (row: ISrsBallotVote) => row.isEligible
    },
    {
      name: 'userName',
      displayName: 'Committee Member',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'organisation',
      displayName: 'Organisation',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'role',
      displayName: 'Role',
      type: TableColumnType.string,
      isSortable: true,
    },
    {
      name: 'country',
      displayName: 'Country',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'votingRights',
      displayName: 'Voting Rights',
      isSortable: true,
      type: TableColumnType.string,
    },
    {
      name: 'vote',
      displayName: 'Vote',
      type: TableColumnType.component,
      component: SrsVoteColumnComponent,
      getComponentInput: (row: ISrsBallotVote) => {
        return {
          vote: row,
          isUserPmOrAboveOrHoSD: this.isUserPmOrAboveOrHoSD,
          isUserCM: this.isUserCM,
          loginUserEmail: this.loginUserEmail,
        };
      },
      isSortable: true,
    },
    {
      name: 'lastUpdated',
      displayName: 'Last Updated',
      type: TableColumnType.component,
      component: SrsHistoryColumnComponent,
      getComponentInput: (row: ISrsBallotVote) => {
        return {
          vote: row,
          isUserPmOrAboveOrHoSD: this.isUserPmOrAboveOrHoSD,
          afterSaveSubject: this.afterSaveSubject,
          ballotInOpenOrDecisionPendingStatus: this.ballotInOpenOrDecisionPendingStatus,
         };
      },
      isSortable: true,
    },
  ];

  updateTableColumns() {
    this.viewVoteTableColumns.forEach((column) => {
      if (column.name === 'checkbox') {
        column.isHidden = !this.isUserPmOrAbove  || !this.ballotInOpenOrDecisionPendingStatus; // Hide checkbox column if isUserCM is true
      }
    });
  }

  ngOnInit() {
    if (this.srsBallotDetail) {
      this.allVotes = this.srsBallotDetail.votes?.map((vote) => ({
        ...vote,
        votingRights: vote.isEligible ? this.eligible : this.notEligible,
      }));
      this.showingVotes = this.allVotes.length > 50
      ? this.allVotes.slice(0, 50)
      : this.allVotes;
      this.totalRecords = this.allVotes.length;
      this.isJoint = this.srsBallotDetail.isJoint;
      this.ballotInOpenOrDecisionPendingStatus = (this.srsBallotDetail?.statusId == SrsBallotStatusId.PSB_OPENED || this.srsBallotDetail?.statusId == SrsBallotStatusId.PSB_DECISION_PENDING);
      this.updateTableColumns();
    }
    this.evaluateLocationDropdownVisibility();
  }

  onFilterChange(value: any, fromVotingRight: boolean) {
    if (fromVotingRight) {
      this.votingRightDropdown = value;

      if (this.locationDropdown) {
        // Apply both filters if locationDropdown is not null or empty
        const filteredByVotingRight = this.filterByVotingRight(value);
        this.showingVotes = this.filterByLocation(
          this.locationDropdown,
          filteredByVotingRight
        );
      } else {
        // Apply only voting rights filter
        this.showingVotes = this.filterByVotingRight(value);
      }
    } else {
      this.locationDropdown = value;

      if (this.votingRightDropdown) {
        // Apply both filters if votingRightDropdown is not null or empty
        const filteredByLocation = this.filterByLocation(value);
        this.showingVotes = this.filterByVotingRight(
          this.votingRightDropdown,
          filteredByLocation
        );
      } else {
        // Apply only location filter
        this.showingVotes = this.filterByLocation(value);
      }
    }
    this.isDropdownChanged = true;
    this.onRowsUpdated(this.showingVotes);
  }

  public filterByVotingRight(
    value: string,
    votes: ISrsBallotVote[] = this.allVotes
  ): ISrsBallotVote[] {
    switch (value) {
      case 'eligible':
        return votes.filter((vote) => vote.isEligible);
      case 'notEligible':
        return votes.filter((vote) => !vote.isEligible);
      default:
        return votes;
    }
  }

  public filterByLocation(
    value: string,
    votes: ISrsBallotVote[] = this.allVotes
  ): ISrsBallotVote[] {
    switch (value) {
      case 'aus-nz':
        return votes.filter(
          (vote) =>
            Country.Australia.includes(vote.country?.toLowerCase()) ||
            Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      case 'au':
        return votes.filter((vote) =>
          Country.Australia.includes(vote.country?.toLowerCase())
        );
      case 'nz':
        return votes.filter((vote) =>
          Country.NewZealand.includes(vote.country?.toLowerCase())
        );
      default:
        return votes;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  evaluateLocationDropdownVisibility() {
    const hasNZCommitteeVotes = this.allVotes.find(
      (vote) =>
        vote.country && Country.NewZealand.includes(vote.country.toLowerCase())
    );
    this.isShowingLocations = hasNZCommitteeVotes != null && this.isJoint;
  }

  onRowsUpdated(updatedRows: ISrsBallotVote[]): void {
    // selected votes for proxy vote
    this.showingVotes = updatedRows;
  }

  onSelectedRowsChanged(updatedRows: ISrsBallotVote[]): void {
    // selected votes for proxy vote
    this.selectedVotes = updatedRows;
    this.selectedVoteCount = this.selectedVotes.length;
  }


  onPageChange(page: number): void {
    this.selectedPageNumber = page;
    const startIndex = (page - 1) * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.allVotes.length);
    const paginatedVotes = this.allVotes.slice(startIndex, endIndex);
    this.showingVotes = paginatedVotes;
  }
  openProxyVoteModal() {
    const modalRef = this._modalService.open(SrsProxyVoteComponent, { size: 'xl', centered: true });
    modalRef.componentInstance.committeeMembers = this.selectedVotes;
    modalRef.result.then(r => {
      if(r) {
        this.afterSave.emit(r);
      }
    })
  }
}
